import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import { useStoresList } from '@/lib/api/stores';
import { hasSkippedOnboarding } from '@/lib/onboarding';
import { isGuidedSetupEnabled, isSetupExemptPath } from '@/lib/setupFeatures';

function GuidedSetupLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
    </div>
  );
}

/**
 * Runs the actual store lookup + redirect. Mounted only when the user has
 * STORES_VIEW so `stores.list` never fires for users lacking that permission.
 */
function GuidedSetupStoresCheck({ orgSlug }: { orgSlug: string }) {
  const { data: storesData, isLoading } = useStoresList({
    pagination: { page: 1, pageSize: 1 },
  });

  if (isLoading) {
    return <GuidedSetupLoader />;
  }

  const hasStores = (storesData?.stores?.length ?? 0) > 0;
  const shouldRedirect = !hasStores && !hasSkippedOnboarding(orgSlug);

  if (shouldRedirect) {
    return <Navigate to={`/org/${orgSlug}/setup`} replace />;
  }

  return <Outlet />;
}

/**
 * Merchants with no store yet are redirected to /org/:slug/setup instead of landing on an
 * empty dashboard — unless they explicitly skipped guided setup for this org, in which case
 * we leave them on the dashboard (setup stays reachable manually at /setup).
 */
export function GuidedSetupGuard() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const location = useLocation();
  const { can, isLoaded } = usePerm();

  const exempt = isSetupExemptPath(location.pathname);

  if (!isGuidedSetupEnabled() || exempt || !orgSlug) {
    return <Outlet />;
  }

  if (!isLoaded) {
    return <GuidedSetupLoader />;
  }

  if (!can(PERMISSIONS.STORES_VIEW)) {
    return <Outlet />;
  }

  return <GuidedSetupStoresCheck orgSlug={orgSlug} />;
}
